import React, { useRef } from 'react';
import PropTypes from 'prop-types';

import Button from '../../../../library/button/button';
import Collapsible from '../../../../library/collapsible/collapsible';
import Tooltip from '../../../../library/tooltip/tooltip';
import { CircleSolidIcon } from '../../../../shared/icons/icons';

import { GET_CANONICAL_SIZES } from '../../../../shared/graphql/product-detail-queries';

import useCanonicalForm from '../../../../pages/product-detail-page/hooks/use-canonical-form/use-canonical-form';
import { useQuery } from '../../../../shared/hooks/hooks';

import {
  ProductProp,
  ProductTypeProp
} from '../../../../shared/utils/prop-types/prop-types';
import { isNotEmptyNullOrUndefined } from '../../../../shared/utils/validators/validators';

import { PRODUCT_TYPES } from '../../../../shared/constants/products';

import styles from './size-table.scss';

function SizeTable(props) {
  const {
    isCanonicalSearch,
    onSizeSelected,
    product,
    productType,
    selectedFitment,
    storeCode
  } = props;
  const canonicalForm = useCanonicalForm(product);
  const { filters } = canonicalForm;

  const baseProduct = useRef(product?.url?.split('/')[2].split('?')[0]);

  const canonicalProductUrl =
    product?.canonicalFallBackUrl || window.location.pathname;

  const { data: canonicalProductData, isLoading } = useQuery(
    GET_CANONICAL_SIZES,
    {
      variables: {
        id: baseProduct.current,
        productType: productType,
        storeCode: storeCode
      }
    }
  );

  const canonicalProduct = canonicalProductData?.product?.canonicalById || {};

  function buildGroupSizeLinks(groupedLinks, product) {
    groupedLinks[product.wheelRimDiameter] =
      groupedLinks[product.wheelRimDiameter] || [];

    if (productType === PRODUCT_TYPES.TIRES) {
      groupedLinks[product.wheelRimDiameter].push({
        code: product.code,
        href: `${canonicalProductUrl}/p/${product.code}`,
        loadIndexSpeedRating: product.loadIndexWithRatingKey || '',
        loadRange: product.loadRange || '',
        size: `${product.tireCrossSection}/${product.tireAspectRatio}-R${product.wheelRimDiameter}`
      });
    } else {
      groupedLinks[product.wheelRimDiameter].push({
        boltPattern: `${product.boltPattern}`,
        code: product.code,
        diameter: `${product.wheelRimDiameter}"`,
        href: `${canonicalProductUrl}/p/${product.code}`,
        width: `${product.wheelWidth}"`
      });
    }

    return groupedLinks;
  }

  const sizeLinkOptions = canonicalProduct?.aggregates?.rangedData?.reduce(
    buildGroupSizeLinks,
    {}
  );

  function getSelectedSizeText() {
    let text = '';

    if (selectedFitment) {
      if (productType === PRODUCT_TYPES.TIRES) {
        text = selectedFitment?.frontTireSize;
      } else {
        text = `${selectedFitment?.frontWheelDiameter}" wheel size`;
      }
    } else {
      if (productType === PRODUCT_TYPES.TIRES) {
        text =
          product?.tireCrossSection &&
          product?.tireAspectRatio &&
          product?.tireWheelRimDiameter
            ? `${product?.tireCrossSection}/${product?.tireAspectRatio}-R${product?.tireWheelRimDiameter}`
            : 'No tire size selected';
      } else {
        text = product?.wheelRimDiameter
          ? `${product?.wheelRimDiameter}" wheel size`
          : 'No wheel size selected';
      }
    }
    return text;
  }

  function getBoltPatternText() {
    let text = '';

    text = product?.boltPatternVal
      ? product?.boltPatternVal
      : 'No bolt pattern selected';

    return text;
  }

  function shouldBeOpen(size) {
    if (productType === PRODUCT_TYPES.TIRES) {
      return size === product?.tireWheelRimDiameter;
    } else {
      return size === product?.wheelRimDiameter;
    }
  }

  function doesMatch(propertiesToMatch) {
    const { code } = propertiesToMatch;

    return code === product?.code;
  }

  function doesFitSelectedFitment(item) {
    return (
      selectedFitment?.frontWheelDiameter == item.diameter &&
      filters?.boltPattern?.find(i => i.value === item.boltPattern)
    );
  }

  return (
    <div>
      <div className={styles['header']}>
        <div>
          <span>
            Selected Size: <Tooltip content="Selected Size" />
          </span>
          <br />
          <span>{getSelectedSizeText()}</span>
        </div>

        {productType === PRODUCT_TYPES.WHEELS && (
          <div>
            <span>
              Bolt Pattern: <Tooltip content="Bolt Pattern" />
            </span>
            <br />
            <span>{getBoltPatternText()}</span>
          </div>
        )}
      </div>
      {isLoading && !canonicalProductData ? null : (
        <div className={styles['collapse-table']}>
          {canonicalProduct?.aggregates?.codes ? (
            Object.entries(sizeLinkOptions).map(size => (
              <div key={size[0]}>
                <Collapsible
                  className={styles['collapsible-size']}
                  isOpenByDefault={shouldBeOpen(size[0])}
                  label={`${size[0]}"`}
                >
                  {productType === PRODUCT_TYPES.WHEELS ? (
                    <div
                      className={styles['wheel-table']}
                      key={`${size[1]}-inch-options`}
                    >
                      <div>
                        <div className={styles['collapse-subtitle']}>
                          <div>Wheel Size</div>
                          <div>Width</div>
                          <div>Bolt Pattern</div>
                        </div>

                        {size[1].map(item => {
                          const isMatch = doesMatch({
                            code: item.code
                          });

                          return (
                            <Button
                              ariaLabel={`${product?.brand} ${product?.name}, wheel size: ${item.diameter}, width: ${item.width}, bolt pattern: ${item.boltPattern}`}
                              className={
                                isMatch
                                  ? styles['wheel-button-match']
                                  : styles['wheel-button']
                              }
                              href={isMatch ? null : item.href}
                              isDisabled={
                                isMatch ||
                                !isCanonicalSearch ||
                                (selectedFitment &&
                                  !doesFitSelectedFitment(item))
                              }
                              key={item.code}
                              onClick={onSizeSelected?.(item)}
                            >
                              <div
                                className={
                                  isMatch
                                    ? styles['wheel-diameter-matching']
                                    : styles['wheel-diameter']
                                }
                              >
                                {isMatch && (
                                  <CircleSolidIcon
                                    className={styles['green-dot']}
                                  />
                                )}
                                {item.diameter}
                              </div>
                              <div>{item.width}</div>
                              <div>{item.boltPattern}</div>
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div
                      className={styles['tire-table']}
                      key={`${size[1]}-inch-options`}
                    >
                      <div>
                        <div>
                          <div className={styles['collapse-subtitle']}>
                            <div>Tire Size</div>
                            <div>Load Index/Speed Rating</div>
                            <div>Load Range</div>
                          </div>
                        </div>

                        {size[1].map(item => {
                          const isMatch = doesMatch({
                            code: item.code
                          });

                          return (
                            <div
                              className={styles['tire-rows']}
                              key={item.code}
                            >
                              {isNotEmptyNullOrUndefined(item) ? (
                                <div className={styles['tire-items']}>
                                  <Button
                                    ariaLabel={`${product?.brand} ${product?.name}, tire size: ${item.size}, load index/speed rating: ${item.loadIndexSpeedRating}, load range: ${item.loadRange}`}
                                    className={
                                      isMatch
                                        ? styles['tire-button-match']
                                        : styles['tire-button']
                                    }
                                    href={isMatch ? null : item.href}
                                    isDisabled={
                                      isMatch ||
                                      !isCanonicalSearch ||
                                      (selectedFitment &&
                                        !doesFitSelectedFitment(item))
                                    }
                                    onClick={onSizeSelected?.(item)}
                                  >
                                    <div>
                                      {isMatch && (
                                        <CircleSolidIcon
                                          className={styles['green-dot']}
                                        />
                                      )}
                                      {item.size}
                                    </div>
                                    <div>{item.loadIndexSpeedRating}</div>
                                    <div>{item.loadRange}</div>
                                  </Button>
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </Collapsible>
              </div>
            ))
          ) : (
            <Collapsible
              className={styles['collapsible-size']}
              isOpenByDefault
              label={`${
                productType === PRODUCT_TYPES.TIRES
                  ? product.tireWheelRimDiameter
                  : product.wheelRimDiameter
              }"`}
            >
              {productType === PRODUCT_TYPES.WHEELS ? (
                <div className={styles['wheel-table']}>
                  <div>
                    <div className={styles['collapse-subtitle']}>
                      <div>Wheel Size</div>
                      <div>Bolt Pattern</div>
                    </div>
                    <Button
                      ariaLabel={`${product?.brand} ${product?.name}, wheel size: ${product.wheelRimDiameter}, bolt pattern: ${product.boltPatternVal}`}
                      className={styles['wheel-button']}
                      isDisabled
                    >
                      <div className={styles['wheel-diameter-matching']}>
                        <CircleSolidIcon className={styles['green-dot']} />
                        {`${product.wheelRimDiameter}"`}
                      </div>
                      <div className={styles['wheel-bolt-pattern']}>
                        {product.boltPatternVal}
                      </div>
                    </Button>
                  </div>
                </div>
              ) : (
                <div className={styles['tire-table']}>
                  <div>
                    <div>
                      <div className={styles['collapse-subtitle']}>
                        Tire Size
                      </div>
                    </div>
                    <div className={styles['tire-rows']}>
                      <div className={styles['tire-items']}>
                        <Button
                          ariaLabel={`${product?.brand} ${product?.name}, tire size: ${product?.tireCrossSection}/${product?.tireAspectRatio}-R${product?.tireWheelRimDiameter}`}
                          className={styles['tire-button-match']}
                          isDisabled
                        >
                          <CircleSolidIcon className={styles['green-dot']} />
                          {product?.tireCrossSection}/{product?.tireAspectRatio}
                          -R
                          {product?.tireWheelRimDiameter}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Collapsible>
          )}
        </div>
      )}
    </div>
  );
}

SizeTable.propTypes = {
  isCanonicalSearch: PropTypes.bool,
  onSizeSelected: PropTypes.func,
  product: ProductProp,
  productType: ProductTypeProp.isRequired,
  selectedFitment: PropTypes.object,
  storeCode: PropTypes.string
};

export default SizeTable;
